/**
 * NFC Logger - Centralized logging for NFC operations
 *
 * Provides structured, consistent logging across all NFC-related code.
 * Logs include timestamps, context, and severity levels for easy debugging.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  context: string;
  message: string;
  data?: unknown;
  timestamp: string;
}

// Enable/disable verbose logging (can be controlled via localStorage for debugging)
const isVerboseEnabled = (): boolean => {
  try {
    return localStorage.getItem('nfc_debug') === 'true';
  } catch {
    return false;
  }
};

const formatTimestamp = (): string => {
  return new Date().toISOString().slice(11, 23); // HH:mm:ss.SSS
};

const formatLogEntry = (entry: LogEntry): string => {
  return `[${entry.timestamp}] [NFC:${entry.context}]`;
};

/**
 * Renders a plain object payload as a string, and passes everything else
 * through untouched — an Error stays an Error so the browser console keeps its
 * stack trace, and primitives already print fine.
 *
 * Nested errors are unwrapped by hand: they carry no enumerable own properties,
 * so JSON.stringify would render `{ error }` as `{"error":{}}`.
 */
const serializeLogData = (data: unknown): unknown => {
  if (data === null || typeof data !== 'object' || data instanceof Error) {
    return data;
  }

  try {
    return JSON.stringify(data, (_key, value) =>
      value instanceof Error ? `${value.name}: ${value.message}` : value,
    );
  } catch {
    return String(data);
  }
};

const log = (
  level: LogLevel,
  context: string,
  message: string,
  data?: unknown,
): void => {
  // Skip debug logs unless verbose mode is enabled
  if (level === 'debug' && !isVerboseEnabled()) {
    return;
  }

  const entry: LogEntry = {
    level,
    context,
    message,
    data,
    timestamp: formatTimestamp(),
  };

  const prefix = formatLogEntry(entry);
  const args: unknown[] = [prefix, message];

  if (data !== undefined) {
    // Serialized rather than passed as an object: the Capacitor console bridge
    // stringifies each argument, so an object reaches logcat as
    // "[object Object]" and the payload — the only part worth reading when
    // debugging on a device — is lost.
    args.push(serializeLogData(data));
  }

  switch (level) {
    case 'debug':
      console.debug(...args);
      break;
    case 'info':
      console.log(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'error':
      console.error(...args);
      break;
  }
};

/**
 * Creates a scoped logger for a specific NFC context
 */
export const createNfcLogger = (context: string) => ({
  debug: (message: string, data?: unknown) =>
    log('debug', context, message, data),
  info: (message: string, data?: unknown) =>
    log('info', context, message, data),
  warn: (message: string, data?: unknown) =>
    log('warn', context, message, data),
  error: (message: string, data?: unknown) =>
    log('error', context, message, data),
});

// Pre-configured loggers for common contexts
export const nfcPluginLogger = createNfcLogger('Plugin');
export const nfcHookLogger = createNfcLogger('Hook');
export const nfcScannerLogger = createNfcLogger('Scanner');
export const nfcSetupLogger = createNfcLogger('Setup');
/**
 * GPS capture triggered by a scan. Lives here — rather than in its own module —
 * so the `nfc_debug` flag turns on every scan-related log at once, whether the
 * scan came from a tag, a QR code or a shortlink.
 */
export const scanLocationLogger = createNfcLogger('ScanLocation');

/**
 * Enable verbose NFC logging (useful for debugging)
 * Call this from browser console: window.enableNfcDebug()
 */
export const enableNfcDebug = (): void => {
  try {
    localStorage.setItem('nfc_debug', 'true');
    console.log('[NFC] Debug logging enabled. Refresh to see all logs.');
  } catch {
    console.warn('[NFC] Could not enable debug logging');
  }
};

/**
 * Disable verbose NFC logging
 */
export const disableNfcDebug = (): void => {
  try {
    localStorage.removeItem('nfc_debug');
    console.log('[NFC] Debug logging disabled.');
  } catch {
    // Ignore
  }
};

// Expose debug functions globally for console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).enableNfcDebug =
    enableNfcDebug;
  (window as unknown as Record<string, unknown>).disableNfcDebug =
    disableNfcDebug;
}
